import React from "react";
import PageTitle from "../common/PageTitle/PageTitle";
import ParaTitle from "../common/ParaTitle/ParaTitle";
import ParaSubtitle from "../common/ParaSubtitle/ParaSubtitle";
import { Paragraph, Pagination, ReusableTable } from "../common";

const example1 = {
    columns: [
        { id: 'feature', label: 'Feature' },
        { id: 'low', label: 'Low Information' },
        { id: 'high', label: 'High Information' },
    ],
    data: [
        { feature: 'Location', low: 'The breakroom', high: 'East Wing breakroom' },
        { feature: 'Identity', low: 'Equipment', high: 'Large coffee brewer' },
        { feature: 'Timing', low: 'Today', high: '10:15 AM' },
        { feature: 'Actionability', low: 'Low (Requires follow-up)', high: 'High (Can be fixed immediately)' },
    ],
}

const example2 = {
    columns: [
        { id: 'feature', label: 'Feature' },
        { id: 'low', label: 'Low Information' },
        { id: 'high', label: 'High Information' },
    ],
    data: [
        { feature: 'What', low: 'The wrong dose', high: '10mg in a 5mg bag' },
        { feature: 'Where', low: 'The floor', high: 'Unit 3-North' },
        { feature: 'Specifics', low: 'None', high: 'Lexapro, Batch #402' },
        { feature: 'Outcome', low: 'Investigation stalls', high: 'Pharmacy can audit the batch ASAP' },
    ],
}

const WritingBriefDescription = () => {
    return (
        <div>
            <PageTitle title='Writing an Effective Brief Description' role='User' />
            <ParaTitle paraTitle='Purpose of Reporting' />
            <Paragraph paragraph='Reporting safety events and "near-misses" is a critical component of our safety culture. Because frontline staff possess a unique perspective on system vulnerabilities, the quality of information provided in these reports directly impacts our ability to implement effective solutions.' />

            <ParaTitle paraTitle='The Importance of Detail' />
            <Paragraph paragraph='To address the root cause of an issue, investigations require specific data. While anonymous reporting is always an option, please be aware that we cannot contact anonymous reporters for follow-up questions. This can sometimes limit the depth of an investigation.' />
            <Paragraph paragraph='To ensure every report—whether anonymous or attributed—can be fully investigated, we have provided the following resources:' />
            <ul>
                <li>
                    The "Actionable Detail" Checklist: A quick-reference guide located in the description field to ensure "Who, What, and Where" are captured.
                </li>
                <li>
                    Case Examples: "Before and After" samples demonstrating how specific details lead to faster problem resolution.
                </li>
            </ul>

            <ParaTitle paraTitle='Objective' />
            <Paragraph paragraph='Our goal is to move beyond data collection and toward active problem-solving. Providing specific, objective details in your description ensures that identified hazards are addressed and that the workplace remains safe for all staff and patients.' />
            <ParaTitle paraTitle='Recommended Checklist for the Brief Description Field' />
            <ul>
                <li>Specific Location: (e.g., Room number, specific workstation, or hallway)</li>
                <li>Exact Timing: (e.g., "During the 07:00 shift change" or "15 minutes after a medication was delivered")</li>
                <li>Equipment/Supplies Involved: (Include brand names, model numbers, or IDs if visible)</li>
                <li>The Sequence of Events: (A brief "first, then, finally" account of what happened)</li>
                <li>Roles Involved: (If you don't want to use names, please list roles, e.g., "The attending physician," "the float nurse," or "the transport tech")</li>
                <li>Environmental Factors: (e.g., Was the unit unusually busy? Was the lighting poor? Was there a software lag?)</li>
                <li>Immediate Action Taken: (What was done in the moment to mitigate the risk or address the error?)</li>
            </ul>

            <ParaTitle paraTitle='Example 1' />
            <ParaSubtitle paraSubtitle='Low Information (Vague & Unactionable)' />
            <Paragraph paragraph='"The equipment in the breakroom is broken and someone almost got hurt today. This unit is always so messy and it’s a safety hazard. Please fix it."' />
            <ul><li>The Problem: Which piece of equipment? What time? What exactly happened? Without a name to call for details, this report likely leads to a dead end.</li></ul>

            <ParaSubtitle paraSubtitle='High Information (Anonymous but Actionable)' />
            <Paragraph paragraph='"At 10:15 AM, the large coffee brewer in the East Wing breakroom leaked boiling water from the base. A transport tech nearly slipped. I unplugged it and put up a sign, but the internal seal looks blown. This is the second time this specific machine has leaked this week."' />
            <ul><li>The Difference: Even without a name, you now know exactly what (coffee brewer), where (East Wing), who (transport tech), and the urgency (second occurrence). You can send a repair tech directly to the spot without needing to talk to the reporter.</li></ul>

            <ParaSubtitle paraSubtitle='Comparison at a Glance' />
            <ReusableTable tableConfig={example1} />


            <ParaTitle paraTitle='Example 2' />
            <ParaSubtitle paraSubtitle='Low Information (Vague & Unactionable)' />
            <Paragraph paragraph='"The pharmacy sent the wrong dose of a med to the floor today. This happens all the time and it’s dangerous. Someone needs to talk to them."' />
            <ul><li>The Problem: Which medication? Which unit did it go to? What was the "wrong" dose vs. the "right" dose? Without a contact name, the investigation stops here because you don't even know which pharmacist or technician was involved.</li></ul>

            <ParaSubtitle paraSubtitle='High Information (Anonymous but Actionable)' />
            <Paragraph paragraph='"During the 09:00 med pass on Unit 3-North, a 10mg dose of Lexapro was delivered in a bag labeled for 5mg. A travel nurse caught the discrepancy before giving it to the patient. The medication was from Batch #402. Please check the rest of that batch in the pharmacy."' />
            <ul><li>The Difference: You have a time (09:00), a location (3-North), a specific drug and dose discrepancy, and a batch number. Even without talking to the reporter, the Pharmacy Manager can immediately pull Batch #402 to see if other bags are mislabeled.</li></ul>

            <ParaSubtitle paraSubtitle='Comparison at a Glance' />
            <ReusableTable tableConfig={example2} />


            <Pagination
                pagination={{
                    prev: { name: 'Adding New Event', link: '/general/adding-new-event' },
                    next: { name: 'Assign Reviewers', link: '/risk-manager/assign-reviewers' }
                }}
            />
        </div>
    );
};

export default WritingBriefDescription;